/**
 * PDP ATC Product Upsell  (+ A/B-test arm selection)
 * -------------------------------------------------------------------------
 * Listens for the cancellable 'pdp:atc-success' CustomEvent dispatched by
 * assets/pdp-hero.js after a successful /cart/add.js for the primary product.
 * Takes ownership (preventDefault), opens the upsell modal, and on
 * accept/dismiss calls e.detail.resume() which opens UpCart + publishes
 * cartUpdate as the default flow would have.
 *
 * TWO SHAPES (same file):
 *   • SINGLE OFFER — one [data-pdp-upsell] root (FOTM-auto or per-PDP
 *     metafield). Original behavior, untouched.
 *   • A/B TEST — multiple [data-pdp-upsell][data-pdp-upsell-arm] roots, one
 *     per arm. On ATC, read window.igData.user.getTestGroup(experiment_id),
 *     reveal the assigned arm (default-arm fallback), wire + open ONLY that
 *     arm. Intelligems owns assignment + analytics; the theme owns the modal.
 *     QA: ?ig_arm=<key> forces an arm on preview themes (IG only runs live).
 *
 * Coexistence with pdp-donation-upsell: bail if e.defaultPrevented is true.
 * Safety net: on any throw we resume() so the shopper is never stranded.
 */
(function () {
  'use strict';

  var SUPPRESSION_DEFAULT = 'teeccino_upsell_seen_v1';

  function $(selector, root) { return (root || document).querySelector(selector); }
  function $$(selector, root) { return Array.from((root || document).querySelectorAll(selector)); }

  function safeSessionGet(key) { try { return window.sessionStorage.getItem(key); } catch (e) { return null; } }
  function safeSessionSet(key, value) { try { window.sessionStorage.setItem(key, value); } catch (e) {} }

  function getQueryParam(name) {
    try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
  }

  function pushDataLayer(eventName, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: eventName };
      if (params) { for (var k in params) { if (Object.prototype.hasOwnProperty.call(params, k)) payload[k] = params[k]; } }
      window.dataLayer.push(payload);
    } catch (e) { /* never block on analytics */ }
  }

  /**
   * Fire an Intelligems custom event (the take-rate goal). IG's exact event
   * method is confirmed by LabSmith at live config; we attempt the documented
   * shapes and no-op gracefully (preview themes / before IG loads). dataLayer
   * mirror is the backstop.
   * TODO(labsmith): confirm canonical IG event method + goal name at config.
   */
  function fireIgEvent(name, props) {
    try {
      var ig = window.intelligems || window.Intelligems || null;
      if (ig) {
        if (typeof ig.track === 'function') { ig.track(name, props || {}); }
        else if (typeof ig.trackEvent === 'function') { ig.trackEvent(name, props || {}); }
        else if (ig.events && typeof ig.events.track === 'function') { ig.events.track(name, props || {}); }
      }
      if (window.igData && typeof window.igData.track === 'function') { window.igData.track(name, props || {}); }
    } catch (e) { /* swallow */ }
    pushDataLayer('ig_' + name, props || {});
  }

  /**
   * IG's igData can populate asynchronously. Wait briefly before reading
   * getTestGroup; fall back if it never arrives. No wait when there's no
   * experiment id (ship-blank) or igData is already present.
   */
  function whenIgReady(experimentId, cb) {
    if (!experimentId) { cb(); return; }
    if (window.igData && window.igData.user) { cb(); return; }
    var waited = 0, step = 60, budget = 1200;
    var t = setInterval(function () {
      waited += step;
      if ((window.igData && window.igData.user) || waited >= budget) { clearInterval(t); cb(); }
    }, step);
  }

  function getTrackingContext(root, controller) {
    var sourceVariantId = '';
    try {
      var pdpVariantInput = document.querySelector(
        'product-info form[action*="/cart/add"] input[name="id"], ' +
        'main form[action*="/cart/add"] input[name="id"]'
      );
      if (pdpVariantInput && pdpVariantInput.value) sourceVariantId = String(pdpVariantInput.value);
    } catch (e) { /* ignore */ }
    if (!sourceVariantId && window.ShopifyAnalytics && window.ShopifyAnalytics.meta) {
      var meta = window.ShopifyAnalytics.meta;
      if (meta.selectedVariantId) sourceVariantId = String(meta.selectedVariantId);
      else if (meta.product && Array.isArray(meta.product.variants) && meta.product.variants.length > 0) {
        sourceVariantId = String(meta.product.variants[0].id);
      }
    }
    var upsellVariantId = controller && typeof controller.getSelectedVariantId === 'function'
      ? (controller.getSelectedVariantId() || '') : '';
    return {
      modal_type: 'product_upsell',
      modal_version: root.getAttribute('data-modal-version') || 'product-v1',
      source_product_id: root.getAttribute('data-source-product-id') || '',
      source_product_handle: root.getAttribute('data-source-product-handle') || '',
      source_variant_id: sourceVariantId,
      upsell_product_id: root.getAttribute('data-upsell-product-id') || '',
      upsell_product_handle: root.getAttribute('data-upsell-product-handle') || '',
      upsell_variant_id: String(upsellVariantId || ''),
      arm_key: root.getAttribute('data-arm-key') || '',
      ig_group_id: root.getAttribute('data-ig-group-id') || '',
      source_mode: root.getAttribute('data-source-mode') || '',
      discount_pct: parseInt(root.getAttribute('data-discount-pct') || '0', 10) || 0,
      campaign: root.getAttribute('data-campaign') || ''
    };
  }

  /**
   * Wire dialog interactions. opts.onAcceptSuccess(ctx) fires after a 200-OK
   * add (used to emit the IG take-rate event in A/B mode).
   */
  function wireDialog(root, opts) {
    opts = opts || {};
    var dialog = $('[data-pdp-upsell-dialog]', root);
    if (!dialog) return null;

    var acceptBtn      = $('[data-pdp-upsell-accept]', dialog);
    // IG take-rate click-goal hook. LabSmith's Intelligems click-goal targets
    // [data-ig-upsell-accept]; we add it ONLY on a real IG-assigned render
    // (opts.igAcceptHook === picked.exposed) so QA / default-fallback /
    // self-offer-suppressed renders never register a take-rate conversion.
    if (opts.igAcceptHook && acceptBtn) acceptBtn.setAttribute('data-ig-upsell-accept', '');
    var dismissBtn     = $('[data-pdp-upsell-dismiss]', dialog);
    var closeBtn       = $('[data-pdp-upsell-close]', dialog);
    var errorSlot      = $('[data-pdp-upsell-error]', dialog);
    var ctaText        = $('[data-pdp-upsell-cta-text]', dialog);
    var variantInputs  = $$('.pdp-upsell__variant-input', dialog);
    var variantLabels  = $$('[data-pdp-upsell-variant]', dialog);

    var supportsDialog = typeof dialog.showModal === 'function';
    var sessionKey = root.getAttribute('data-upsell-session-key') || SUPPRESSION_DEFAULT;
    var ctaPrefix  = root.getAttribute('data-cta-prefix') || 'Add to your order';

    var currentOpener = null, currentResume = null, decided = false, publicController = null;

    function lockBody()   { document.body.classList.add('pdp-upsell-locked'); }
    function unlockBody() { document.body.classList.remove('pdp-upsell-locked'); }

    function getActiveVariantInput() {
      if (variantInputs.length === 1 && variantInputs[0].type === 'hidden') return variantInputs[0];
      for (var i = 0; i < variantInputs.length; i++) { if (variantInputs[i].checked && !variantInputs[i].disabled) return variantInputs[i]; }
      for (var j = 0; j < variantInputs.length; j++) { if (!variantInputs[j].disabled) return variantInputs[j]; }
      return variantInputs[0] || null;
    }
    function getSelectedVariantId() { var input = getActiveVariantInput(); return input ? input.value : null; }
    function getSelectedPriceDisplay() {
      var input = getActiveVariantInput();
      if (!input) return '';
      return input.getAttribute('data-variant-effective-display') || input.getAttribute('data-variant-price-display') || '';
    }
    function getAllVariantIds() { return variantInputs.map(function (i) { return String(i.value); }).filter(Boolean); }

    function updateCtaLabel() {
      if (!ctaText) return;
      var price = getSelectedPriceDisplay();
      ctaText.textContent = price ? (ctaPrefix + ' — ' + price) : ctaPrefix;
    }
    function syncVariantSelectedClass() {
      variantLabels.forEach(function (label) {
        var input = $('.pdp-upsell__variant-input', label);
        if (input && input.checked) label.classList.add('is-selected'); else label.classList.remove('is-selected');
      });
    }
    variantInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        syncVariantSelectedClass(); updateCtaLabel();
        if (errorSlot) { errorSlot.hidden = true; errorSlot.textContent = ''; }
      });
    });
    syncVariantSelectedClass(); updateCtaLabel();

    function open(opener, resume) {
      currentOpener = opener || null;
      currentResume = resume || function () {};
      decided = false;
      if (errorSlot) { errorSlot.hidden = true; errorSlot.textContent = ''; }
      updateCtaLabel();
      if (supportsDialog) {
        try { dialog.showModal(); } catch (e) { dialog.setAttribute('open', ''); dialog.classList.add('is-fallback-open'); }
      } else { dialog.setAttribute('open', ''); dialog.classList.add('is-fallback-open'); }
      lockBody();
      pushDataLayer('upsell_modal_view', getTrackingContext(root, publicController));
      requestAnimationFrame(function () { if (closeBtn && typeof closeBtn.focus === 'function') closeBtn.focus(); });
    }

    function close(reason) {
      if (reason && reason !== 'silent' && reason !== 'yes') {
        var dismissCtx = getTrackingContext(root, publicController);
        dismissCtx.dismiss_reason = reason;
        pushDataLayer('upsell_modal_dismiss', dismissCtx);
      }
      if (supportsDialog && dialog.open) { try { dialog.close(); } catch (e) {} }
      dialog.removeAttribute('open'); dialog.classList.remove('is-fallback-open'); unlockBody();
      if (currentOpener && typeof currentOpener.focus === 'function') requestAnimationFrame(function () { currentOpener.focus(); });
      if (reason && reason !== 'silent') safeSessionSet(sessionKey, '1');
      if (!decided && typeof currentResume === 'function') {
        decided = true;
        try { currentResume(); } catch (e) { console.error('[pdp-upsell] resume() threw', e); }
      }
    }

    function showError(message) {
      if (!errorSlot) return;
      errorSlot.textContent = message || 'Could not add — please try again.';
      errorSlot.hidden = false;
    }

    function addUpsellToCart() {
      var variantId = getSelectedVariantId();
      if (!variantId) { showError('Please choose an option.'); return Promise.reject(new Error('no variant id selected')); }
      var ctx = getTrackingContext(root, publicController);
      var formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      var ts = new Date().toISOString();
      formData.append('properties[_upsell_modal_v]',          ctx.modal_version);
      formData.append('properties[_upsell_source_product_id]', ctx.source_product_id);
      formData.append('properties[_upsell_source_variant_id]', ctx.source_variant_id);
      formData.append('properties[_upsell_campaign]',          ctx.campaign);
      formData.append('properties[_upsell_source_mode]',       ctx.source_mode);
      formData.append('properties[_upsell_discount_pct]',      String(ctx.discount_pct));
      if (ctx.arm_key) formData.append('properties[_upsell_arm]', ctx.arm_key);
      formData.append('properties[_upsell_accepted_at]',       ts);
      var endpoint = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
        ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js';
      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData, credentials: 'same-origin'
      }).then(function (res) { return res.json().then(function (body) { return { ok: res.ok, status: res.status, body: body }; }); });
    }

    if (acceptBtn) {
      acceptBtn.addEventListener('click', function () {
        if (acceptBtn.disabled) return;
        acceptBtn.disabled = true; acceptBtn.setAttribute('aria-busy', 'true');
        if (errorSlot) { errorSlot.hidden = true; errorSlot.textContent = ''; }
        pushDataLayer('upsell_modal_accept', getTrackingContext(root, publicController));
        addUpsellToCart()
          .then(function (result) {
            if (!result.ok) {
              var msg = (result.body && (result.body.description || result.body.message)) || 'Could not add — please try again.';
              showError(msg); return;
            }
            var successCtx = getTrackingContext(root, publicController);
            successCtx.cart_line_id = (result.body && result.body.id) ? String(result.body.id) : '';
            successCtx.cart_line_key = (result.body && result.body.key) ? String(result.body.key) : '';
            pushDataLayer('upsell_modal_accept_success', successCtx);
            if (typeof opts.onAcceptSuccess === 'function') { try { opts.onAcceptSuccess(successCtx); } catch (e) {} }
            close('yes');
          })
          .catch(function (err) { console.error('[pdp-upsell] add failed:', err); showError('Network error — please try again.'); })
          .finally(function () { acceptBtn.disabled = false; acceptBtn.removeAttribute('aria-busy'); });
      });
    }

    if (dismissBtn) dismissBtn.addEventListener('click', function () { close('no'); });
    if (closeBtn)   closeBtn.addEventListener('click',   function () { close('x'); });
    dialog.addEventListener('cancel', function (e) { e.preventDefault(); close('cancel'); });
    dialog.addEventListener('click', function (e) { if (e.target === dialog) close('backdrop'); });
    if (!supportsDialog) {
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && dialog.hasAttribute('open')) close('cancel'); });
    }

    publicController = {
      open: open, close: close,
      isOpen: function () { return (supportsDialog && dialog.open) || dialog.hasAttribute('open'); },
      getSessionKey: function () { return sessionKey; },
      getSelectedVariantId: getSelectedVariantId,
      getAllVariantIds: getAllVariantIds,
      getContext: function () { return getTrackingContext(root, publicController); },
      root: root
    };
    return publicController;
  }

  function anyUpsellVariantInCart(variantIds) {
    if (!variantIds || variantIds.length === 0) return Promise.resolve(false);
    var endpoint = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      ? window.Shopify.routes.root + 'cart.js' : '/cart.js';
    return fetch(endpoint, { headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return false;
        return cart.items.some(function (it) { return variantIds.indexOf(String(it.id)) !== -1; });
      }).catch(function () { return false; });
  }

  /* ---------- A/B arm selection ---------- */

  function pickArmRoot(armRoots) {
    var experimentId = armRoots[0].getAttribute('data-experiment-id') || '';
    var defaultArm   = armRoots[0].getAttribute('data-default-arm-key') || '';

    function byKey(key) {
      if (!key) return null;
      for (var i = 0; i < armRoots.length; i++) { if (armRoots[i].getAttribute('data-arm-key') === key) return armRoots[i]; }
      return null;
    }
    function byGroupId(id) {
      if (id === null || id === undefined || id === '') return null;
      var s = String(id);
      for (var i = 0; i < armRoots.length; i++) { if (String(armRoots[i].getAttribute('data-ig-group-id')) === s) return armRoots[i]; }
      return null;
    }

    // QA override (preview can't run IG): ?ig_arm=coffee
    var qa = getQueryParam('ig_arm');
    if (qa) { var qaRoot = byKey(qa); if (qaRoot) return { root: qaRoot, exposed: false }; }

    if (experimentId && window.igData && window.igData.user && typeof window.igData.user.getTestGroup === 'function') {
      try {
        var group = window.igData.user.getTestGroup(experimentId);
        if (group) {
          var byId = byGroupId(group.id);
          if (byId) return { root: byId, exposed: true };
          if (group.name) { var byName = byKey(group.name); if (byName) return { root: byName, exposed: true }; }
        }
      } catch (e) { /* fall through */ }
    }
    return { root: byKey(defaultArm) || armRoots[0], exposed: false };
  }

  function boot() {
    var roots = $$('[data-pdp-upsell]');
    if (roots.length === 0) return;

    var armRoots = roots.filter(function (r) { return r.hasAttribute('data-pdp-upsell-arm'); });
    var forceShow = getQueryParam('pdp_upsell_force') === '1';

    /* ----- A/B TEST shape ----- */
    if (armRoots.length > 0) {
      var experimentId = armRoots[0].getAttribute('data-experiment-id') || '';
      var sessionKey   = armRoots[0].getAttribute('data-upsell-session-key') || SUPPRESSION_DEFAULT;

      document.addEventListener('pdp:atc-success', function (e) {
        try {
          if (e.defaultPrevented) return;
          if (!forceShow && safeSessionGet(sessionKey) === '1') return;
          e.preventDefault();
          var resume = (e.detail && typeof e.detail.resume === 'function') ? e.detail.resume : function () {};
          var opener = (e.detail && e.detail.atcButton) || document.activeElement || null;

          whenIgReady(experimentId, function () {
            var picked = pickArmRoot(armRoots);
            var armRoot = picked.root;
            if (!armRoot) { try { resume(); } catch (e2) {} return; }

            // Self-offer suppression: never upsell the format already being added.
            // Suppress, do NOT reassign, do NOT count an exposure.
            var sourceId = armRoot.getAttribute('data-source-product-id') || '';
            var offerId  = armRoot.getAttribute('data-upsell-product-id') || '';
            if (sourceId && offerId && String(sourceId) === String(offerId)) {
              safeSessionSet(sessionKey, '1');
              pushDataLayer('upsell_suppressed_self_offer', { arm_key: armRoot.getAttribute('data-arm-key') || '' });
              try { resume(); } catch (e3) {}
              return;
            }

            var ctrl = wireDialog(armRoot, {
              onAcceptSuccess: function (ctx) { fireIgEvent('upsell_accept', ctx); },
              igAcceptHook: picked.exposed
            });
            if (!ctrl) { try { resume(); } catch (e4) {} return; }

            anyUpsellVariantInCart(ctrl.getAllVariantIds()).then(function (alreadyIn) {
              if (alreadyIn) {
                safeSessionSet(sessionKey, '1');
                try { resume(); } catch (e5) {}
                return;
              }
              ctrl.open(opener, resume);
              // Exposure counts only on a real IG-assigned render (QA/default fallback does not).
              if (picked.exposed) fireIgEvent('upsell_exposure', ctrl.getContext());
            }).catch(function (err) {
              console.error('[pdp-upsell] cart check failed — default flow', err);
              try { resume(); } catch (e6) {}
            });
          });
        } catch (outer) {
          console.error('[pdp-upsell] AB listener error', outer);
          try { if (e.detail && e.detail.resume) e.detail.resume(); } catch (e7) {}
        }
      });
      return;
    }

    /* ----- SINGLE OFFER shape (original behavior) ----- */
    var controller = wireDialog(roots[0]);
    if (!controller) return;

    document.addEventListener('pdp:atc-success', function (e) {
      try {
        if (e.defaultPrevented) return;
        if (safeSessionGet(controller.getSessionKey()) === '1') return;
        e.preventDefault();
        var resume = (e.detail && typeof e.detail.resume === 'function') ? e.detail.resume : function () {};
        var opener = (e.detail && e.detail.atcButton) || document.activeElement || null;
        anyUpsellVariantInCart(controller.getAllVariantIds()).then(function (alreadyIn) {
          if (alreadyIn) {
            safeSessionSet(controller.getSessionKey(), '1');
            try { resume(); } catch (err) {}
            return;
          }
          controller.open(opener, resume);
        }).catch(function (err) {
          console.error('[pdp-upsell] cart check failed — default flow', err);
          try { resume(); } catch (e2) {}
        });
      } catch (outer) { console.error('[pdp-upsell] listener error', outer); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
